"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CookieConsent = void 0;
const businessapp_configuration_1 = __importDefault(require("@trustpilot/businessapp-configuration"));
const storage_1 = require("./storage");
const isEssentialCookie = (id) => [
    "tp-b2b-access-token",
    "tp-b2b-refresh-token",
    "tp-b2b-simplicity-data-sent",
    "tp-b2b-current-language",
    "tp-b2b-selected-business-unit-id",
    "appshell-notifications-notifications-seen",
    "appshell-notifications-acknowledged-whats-new",
    "TP.um",
    "TP.uuid",
].includes(id);
const injectOneTrustScript = (locale) => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = !0;
    script.src = "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";
    script.setAttribute("data-language", locale);
    script.setAttribute("data-domain-script", businessapp_configuration_1.default.oneTrustScript);
    // The following id is used to prevent adding the script multiple times.
    script.id = "oneTrustScript";
    script.addEventListener("error", (e) => console.log(`Failed to insert OneTrust script.${e}`));
    document.head.appendChild(script);
};
const init = ({ locale }) => {
    const isAutomatedTest = navigator.userAgent.includes("Ghost Inspector");
    if (isAutomatedTest) {
        return;
    }
    // https://community.cookiepro.com/s/article/UUID-29158b4e-22f6-0067-aa36-94f3b8cf3561?topicId=0TO1Q000000srd9WAA
    if (typeof window.OptanonWrapper === "undefined") {
        window.OptanonWrapper = () => {
            const event = new CustomEvent("OptanonWrapper");
            document.dispatchEvent(event);
        };
    }
    if (!isOneTrustScriptInjected()) {
        injectOneTrustScript(locale);
    }
};
const isOneTrustScriptInjected = () => Boolean(document.getElementById("oneTrustScript"));
const ensureOneTrustSdkLoaded = () => Promise.race([
    new Promise((resolve) => {
        if (window.Optanon && window.OptanonActiveGroups) {
            // @ts-ignore
            resolve();
        }
        else {
            document.addEventListener("OptanonWrapper", resolve, { once: true });
        }
    }),
    new Promise((_, reject) => setTimeout(reject, 5000)),
]).catch(() => {
    console.log("Timeout loading OneTrust script");
    window.Optanon = {
        GetDomainData: () => ({ Groups: [] }),
        ToggleInfoDisplay: () => {
            throw new Error();
        },
    };
    window.OptanonActiveGroups = "";
});
const hasCategoryConsent = async (categoryId) => {
    await ensureOneTrustSdkLoaded();
    const consentGroups = window.OptanonActiveGroups.split(",").filter((group) => group);
    return consentGroups.includes(categoryId);
};
const matchesCookieNamePattern = (cookieName, itemId) => {
    if (cookieName.indexOf("{id}") === -1) {
        // Cookie name does not contain {id} - so it is not a cookie name pattern
        return false;
    }
    const reg = new RegExp(`^${cookieName.replace(/{id}/g, "[0-9a-fA-F]{24}")}$`);
    return Boolean(itemId.match(reg));
};
const canSetValue = async (itemId) => {
    if (isEssentialCookie(itemId)) {
        return true;
    }
    await ensureOneTrustSdkLoaded();
    const { Groups: OptanonGroups } = window.Optanon.GetDomainData();
    let oneTrustGroup;
    const groupWithExactMatch = OptanonGroups.find(({ Cookies }) => Cookies && Cookies.some(({ Name }) => Name === itemId));
    if (groupWithExactMatch) {
        oneTrustGroup = groupWithExactMatch;
    }
    else {
        oneTrustGroup =
            OptanonGroups.find(({ Cookies }) => Cookies &&
                Cookies.some(({ Name }) => matchesCookieNamePattern(Name, itemId))) || {};
    }
    const groupId = oneTrustGroup.OptanonGroupId
        ? oneTrustGroup.OptanonGroupId
        : "";
    if (!groupId) {
        console.error(`Error setting local storage. The item ${itemId} is not in OneTrust whitelist`);
        return false;
    }
    const hasConsent = await hasCategoryConsent(groupId);
    return hasConsent;
};
const showCookiesPreferences = async () => {
    await ensureOneTrustSdkLoaded();
    window.Optanon.ToggleInfoDisplay();
};
// Helper method for being notified the first time consent is given to a particular cookie category
// (invokes callback immediately if consent is given already). Useful for irreversible one time operations
// such as including a script tag.
const onceConsentGiven = async (categoryId, callback) => {
    if (await hasCategoryConsent(categoryId)) {
        callback();
        return;
    }
    const callbackWrapper = async () => {
        if (await hasCategoryConsent(categoryId)) {
            callback();
            document.removeEventListener("OptanonWrapper", callbackWrapper);
        }
    };
    document.addEventListener("OptanonWrapper", callbackWrapper);
};
const onConsentChange = async (callback) => {
    await ensureOneTrustSdkLoaded();
    // https://developer.onetrust.com/onetrust/docs/javascript-api
    window.OneTrust?.OnConsentChanged((e) => {
        callback(e);
    });
};
const getNamesOfAllCookiesInBrowser = () => {
    const cookies = document.cookie.split("; ");
    return cookies.map((cookie) => cookie.split("=")[0]);
};
const getNamesOfAllAllowedCookies = async () => {
    await ensureOneTrustSdkLoaded();
    const { Groups } = window.Optanon.GetDomainData();
    const allowedGroups = window.OptanonActiveGroups.split(",").filter(Boolean);
    const allowedGroupsData = Groups.filter(({ OptanonGroupId = "" }) => allowedGroups.includes(OptanonGroupId));
    const allowedCookies = allowedGroupsData.reduce((acc, { Cookies }) => {
        if (Cookies) {
            const cookies = Cookies.map(({ Name }) => Name);
            return [...acc, ...cookies];
        }
        return acc;
    }, []);
    return allowedCookies;
};
const getNamesOfNotAllowedCookiesInBrowser = async () => {
    await ensureOneTrustSdkLoaded();
    const allBrowserCookies = getNamesOfAllCookiesInBrowser();
    const allowedCookies = await getNamesOfAllAllowedCookies();
    return allBrowserCookies.filter((c) => !allowedCookies.some((allowedCookie) => c === allowedCookie));
};
const getNamesOfNotAllowedLocalStorageItems = async () => {
    await ensureOneTrustSdkLoaded();
    const allowedCookies = await getNamesOfAllAllowedCookies();
    const localStorageKeys = (0, storage_1.getLocalStorageKeys)();
    return localStorageKeys.filter((c) => !allowedCookies.some((allowedLocalStorageItem) => c === allowedLocalStorageItem));
};
const getNamesOfNotAllowedSessionStorageItems = async () => {
    await ensureOneTrustSdkLoaded();
    const allowedCookies = await getNamesOfAllAllowedCookies();
    const sessionStorageKeys = await (0, storage_1.getSessionStorageKeys)();
    return sessionStorageKeys.filter((c) => !allowedCookies.some((allowedSessionStorageItem) => c === allowedSessionStorageItem));
};
const removeCookies = (cookies) => {
    cookies.forEach((cookie) => {
        document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; domain=${businessapp_configuration_1.default.CookieDomain}`;
    });
};
const removeLocalStorageItems = (items) => {
    items.forEach((item) => {
        localStorage.removeItem(item);
    });
};
const removeSessionStorageItems = (items) => {
    items.forEach((item) => {
        sessionStorage.removeItem(item);
    });
};
const removeAllBrowserData = async () => {
    const notAllowedCookies = await getNamesOfNotAllowedCookiesInBrowser();
    const notAllowedLocalStorageItems = await getNamesOfNotAllowedLocalStorageItems();
    const notAllowedSessionStorageItems = await getNamesOfNotAllowedSessionStorageItems();
    removeCookies(notAllowedCookies);
    removeLocalStorageItems(notAllowedLocalStorageItems);
    removeSessionStorageItems(notAllowedSessionStorageItems);
};
exports.CookieConsent = {
    init,
    hasCategoryConsent,
    canSetValue,
    showCookiesPreferences,
    onceConsentGiven,
    onConsentChange,
    isOneTrustScriptInjected,
    getNamesOfAllCookiesInBrowser,
    getNamesOfAllAllowedCookies,
    getNamesOfNotAllowedCookiesInBrowser,
    getNamesOfNotAllowedLocalStorageItems,
    getNamesOfNotAllowedSessionStorageItems,
    removeCookies,
    removeLocalStorageItems,
    removeSessionStorageItems,
    removeAllBrowserData,
};
//# sourceMappingURL=cookieConsent.js.map